// Alerts shown in "Riesgo y alertas" and counted in the chamber panel. They
// are computed from the data on every render (never stored); only the ids of
// dismissed alerts are persisted, so the ids must be deterministic.
import { type DeadlineStatus, deadlineStatus } from "./deadlines";
import { type DossierField, missingFields } from "./dossier";
import { computeNoticeObligations, type NoticeObligation } from "./notices";
import { type AutomaticRiskReason, automaticRiskReason, effectiveRiskLevel } from "./risk";
import { exceedsCashLimit, type Thresholds, withVat } from "./thresholds";
import type { Client, IsoDate, Operation } from "./types";

export type AlertType =
  | "individual_notice"
  | "accumulated_notice"
  | "cash_limit_exceeded"
  | "high_risk_client"
  | "incomplete_dossier";

export type AlertSeverity = "high" | "medium";

interface AlertBase {
  id: string;
  severity: AlertSeverity;
  clientId: string;
}

export type Alert =
  | (AlertBase & {
      type: "individual_notice" | "accumulated_notice";
      notice: NoticeObligation;
      deadlineStatus: DeadlineStatus;
    })
  | (AlertBase & {
      type: "cash_limit_exceeded";
      operationId: string;
      date: IsoDate;
      amountWithVat: number;
    })
  | (AlertBase & {
      type: "high_risk_client";
      pep: boolean;
      automaticReason: AutomaticRiskReason | null;
    })
  | (AlertBase & { type: "incomplete_dossier"; missing: DossierField[] });

export interface AlertInput {
  clients: readonly Client[];
  operations: readonly Operation[];
  thresholds: Thresholds;
  dismissedIds: Iterable<string>;
  today: IsoDate;
  /** Pass precomputed notices to avoid recomputing them. */
  notices?: readonly NoticeObligation[];
}

export function computeAlerts(input: AlertInput): Alert[] {
  const { clients, operations, thresholds, today } = input;
  const notices = input.notices ?? computeNoticeObligations(operations, thresholds);
  const alerts: Alert[] = [];

  for (const notice of notices) {
    if (notice.filed) continue;
    alerts.push({
      id: notice.id,
      type: notice.kind === "individual" ? "individual_notice" : "accumulated_notice",
      severity: "high",
      clientId: notice.clientId,
      notice,
      deadlineStatus: deadlineStatus(notice.deadline, today),
    });
  }

  for (const op of operations) {
    if (exceedsCashLimit(op, thresholds)) {
      alerts.push({
        id: `cash-${op.id}`,
        type: "cash_limit_exceeded",
        severity: "high",
        clientId: op.clientId,
        operationId: op.id,
        date: op.date,
        amountWithVat: withVat(op.amount),
      });
    }
  }

  const identifiedClients = new Set(
    operations.filter((op) => op.amount >= thresholds.identification).map((op) => op.clientId),
  );
  for (const client of clients) {
    if (effectiveRiskLevel(client) === "high") {
      alerts.push({
        id: `risk-${client.id}`,
        type: "high_risk_client",
        severity: "medium",
        clientId: client.id,
        pep: client.pep,
        automaticReason: automaticRiskReason(client),
      });
    }
    if (identifiedClients.has(client.id)) {
      const missing = missingFields(client);
      if (missing.length > 0) {
        alerts.push({
          id: `dossier-${client.id}`,
          type: "incomplete_dossier",
          severity: "medium",
          clientId: client.id,
          missing,
        });
      }
    }
  }

  const dismissed = new Set(input.dismissedIds);
  return alerts.filter((a) => !dismissed.has(a.id)).sort(byPriority);
}

const severityRank: Record<AlertSeverity, number> = { high: 0, medium: 1 };

function byPriority(a: Alert, b: Alert): number {
  const bySeverity = severityRank[a.severity] - severityRank[b.severity];
  if (bySeverity !== 0) return bySeverity;
  const deadlineA = "notice" in a ? a.notice.deadline : "9999-12-31";
  const deadlineB = "notice" in b ? b.notice.deadline : "9999-12-31";
  return deadlineA.localeCompare(deadlineB) || a.id.localeCompare(b.id);
}
